/**
 * LIVE CLASS screen (F3-G): pre-join with session info + countdown until
 * the window opens + roster (teacher sidebar). "Enter" asks the server for
 * the descriptor (`/join`, window + access authority) and, depending on
 * mode, embeds the Jitsi iframe or opens the external link. If the server
 * rejects due to the window, it shows a friendly state. When there's a
 * recording, it's rewatched with the standard player.
 */
import { type Component, createMemo, createSignal, For, Match, Show, Switch } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ArrowLeft, CalendarClock, ExternalLink, Radio, Users, Video } from "@fleet/icons";
import {
  attachIdentity,
  formatCountdown,
  joinWindowState,
  sortByIdentityName,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { NotFoundState } from "@/components/ui/not-found-state";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { EnVivoBadge } from "./en-vivo-badge";
import { JitsiEmbed } from "./jitsi-embed";
import { RecordingPlayer } from "./recording-player";
import { type JoinResponse, liveApi, type LiveApi } from "../lib/live-api";
import { useNow } from "../hooks/use-now";
import { useCohort, useLiveSession } from "../hooks/use-live-data";
import { useCohortRoster } from "@/features/academy/hooks/use-cohort-data";
import { useCohortIdentities } from "@/features/academy/hooks/use-org-identities";

type Stage = "idle" | "joining" | "embed" | "external" | "denied";

/** `HH:MM` from an `HH:MM:SS` (or empty). */
const formatHm = (v: string | null | undefined) => (v ?? "").slice(0, 5);

// Technical window.open arguments (not UI text).
const TAB_TARGET = "_blank";
// oxlint-disable-next-line lingui/no-unlocalized-strings -- window.open features, not prose
const TAB_FEATURES = "noopener,noreferrer";

export const LiveJoinScreen: Component<{
  sessionId: string;
  api?: LiveApi;
  onBack?: () => void;
}> = (props) => {
  const { t } = useI18n();
  const session = useSession();
  const { db } = usePowerSyncContext();
  const api = () => props.api ?? liveApi;
  const now = useNow();

  const sessionQuery = useLiveSession(() => props.sessionId);
  const row = () => sessionQuery.data?.[0];

  // LF-B: a not-yet-synced session used to flash "not available" when
  // entering via deep-link; canonical loading/not-found/offline resolution.
  const resolution = createEntityResolution({
    db,
    hasEntity: () => Boolean(row()),
    isPending: () => sessionQuery.isLoading,
  });
  const cohortQuery = useCohort(() => row()?.cohort_id ?? undefined);
  const rosterQuery = useCohortRoster(() => row()?.cohort_id ?? undefined);
  // D3 (plan 06 · E3): identity no longer comes from the roster's local join.
  const people = useCohortIdentities(() => row()?.cohort_id ?? undefined);

  const times = () => ({
    date: row()?.date,
    startTime: row()?.start_time,
    endTime: row()?.end_time,
  });
  const windowState = createMemo(() => joinWindowState(now(), times()));
  const cohortName = () => cohortQuery.data?.[0]?.name ?? "";
  const displayName = () => session.data?.user?.name ?? session.data?.user?.email ?? undefined;
  const roster = createMemo(() =>
    sortByIdentityName(
      attachIdentity(rosterQuery.data ?? [], people.identities(), (r) => r.user_id),
      (r) => r.user_id ?? r.id,
    ),
  );

  const [stage, setStage] = createSignal<Stage>("idle");
  const [join, setJoin] = createSignal<JoinResponse | null>(null);
  const [denied, setDenied] = createSignal<{ reason: string; opensAt: string | null } | null>(null);
  const [error, setError] = createSignal<string | null>(null);

  const enter = async () => {
    setStage("joining");
    setError(null);
    setDenied(null);
    try {
      const result = await api().join(props.sessionId);
      if (!result.ok) {
        setDenied({ reason: result.reason, opensAt: result.opensAt });
        setStage("denied");
        return;
      }
      setJoin(result.data);
      if (result.data.mode === "embed" && result.data.embed) {
        setStage("embed");
      } else if (result.data.mode === "external" && result.data.joinUrl) {
        globalThis.open?.(result.data.joinUrl, TAB_TARGET, TAB_FEATURES);
        setStage("external");
      } else {
        setError(t(msg`This class has no valid meeting configured.`));
        setStage("idle");
      }
    } catch {
      setError(t(msg`Could not connect to the live class. Try again.`));
      setStage("idle");
    }
  };

  const leaveEmbed = () => {
    setStage("idle");
    setJoin(null);
  };

  return (
    <div class="mx-auto w-full max-w-5xl space-y-4 p-4" data-testid="live-join-screen">
      <Show when={props.onBack}>
        <button
          type="button"
          class="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          data-testid="live-back"
          onClick={() => props.onBack?.()}
        >
          <ArrowLeft class="h-4 w-4" /> {t(msg`Back`)}
        </button>
      </Show>

      <Show
        when={row()}
        fallback={
          <Show
            when={resolution() !== "loading"}
            fallback={<Skeleton class="h-64 w-full rounded-2xl" />}
          >
            <div data-testid="live-not-found">
              <NotFoundState
                variant={resolution() === "offline" ? "offline" : "not-found"}
                title={t(msg`Class not available`)}
                description={
                  resolution() === "offline"
                    ? t(
                        msg`You're offline and this class hasn't synced to this device yet. Reconnect and try again.`,
                      )
                    : t(msg`This class session is not available.`)
                }
                primaryAction={
                  props.onBack
                    ? { label: t(msg`Back`), onClick: () => props.onBack?.() }
                    : undefined
                }
              />
            </div>
          </Show>
        }
      >
        <div class="grid gap-4 md:grid-cols-[1fr_18rem]">
          {/* Main column */}
          <div class="space-y-4">
            {/* Header */}
            <header class="space-y-1">
              <div class="flex items-center gap-2">
                <Radio class="h-5 w-5 text-primary" />
                <h1 class="text-lg font-bold" data-testid="live-topic">
                  {row()?.topic ?? t(msg`Live class`)}
                </h1>
                <EnVivoBadge times={times()} />
              </div>
              <p class="text-sm text-muted-foreground" data-testid="live-subtitle">
                {cohortName()} · {row()?.date} · {formatHm(row()?.start_time)}–
                {formatHm(row()?.end_time)}
              </p>
            </header>

            <Switch>
              {/* Embedded Jitsi */}
              <Match when={stage() === "embed" && join()?.embed}>
                <div class="space-y-2">
                  <JitsiEmbed
                    domain={join()!.embed!.domain}
                    roomName={join()!.embed!.room}
                    displayName={displayName()}
                    onLeave={leaveEmbed}
                  />
                  <Button variant="outline" size="sm" data-testid="live-leave" onClick={leaveEmbed}>
                    {t(msg`Leave class`)}
                  </Button>
                </div>
              </Match>

              {/* External link opened in another tab */}
              <Match when={stage() === "external"}>
                <div
                  class="flex flex-col items-start gap-3 rounded-2xl border border-border p-6"
                  data-testid="live-external"
                >
                  <p class="flex items-center gap-2 text-sm">
                    <ExternalLink class="h-4 w-4 text-accent" />
                    {(() => {
                      const provider = join()?.provider ?? "";
                      return t(msg`The class runs on ${provider}. It opened in a new tab.`);
                    })()}
                  </p>
                  <Show when={join()?.joinUrl}>
                    <Button
                      size="sm"
                      data-testid="live-reopen"
                      onClick={() => globalThis.open?.(join()!.joinUrl, TAB_TARGET, TAB_FEATURES)}
                    >
                      {t(msg`Open again`)}
                    </Button>
                  </Show>
                </div>
              </Match>

              {/* Rejected due to window / cancellation */}
              <Match when={stage() === "denied"}>
                <div
                  class="space-y-2 rounded-2xl border border-warning/40 bg-warning/10 p-6"
                  data-testid="live-denied"
                >
                  <p class="flex items-center gap-2 text-sm font-medium">
                    <CalendarClock class="h-4 w-4" />
                    <Show
                      when={denied()?.reason === "cancelled"}
                      fallback={t(msg`This class is not open yet.`)}
                    >
                      {t(msg`This class was cancelled.`)}
                    </Show>
                  </p>
                  <Show when={denied()?.opensAt}>
                    <p class="text-xs text-muted-foreground" data-testid="live-opens-at">
                      {(() => {
                        const opensAt = (denied()?.opensAt ?? "").replace("T", " ");
                        return t(msg`It opens at ${opensAt}`);
                      })()}
                    </p>
                  </Show>
                  <Button variant="outline" size="sm" onClick={() => setStage("idle")}>
                    {t(msg`Back`)}
                  </Button>
                </div>
              </Match>

              {/* Pre-join */}
              <Match when={stage() === "idle" || stage() === "joining"}>
                <div
                  class="space-y-4 rounded-2xl border border-border p-6"
                  data-testid="live-prejoin"
                >
                  <Switch>
                    <Match when={windowState().phase === "before"}>
                      <div class="space-y-1 text-center">
                        <p class="text-sm text-muted-foreground">{t(msg`The class opens in`)}</p>
                        <p class="text-2xl font-bold tabular-nums" data-testid="live-countdown">
                          {formatCountdown(windowState().msUntilOpen)}
                        </p>
                      </div>
                    </Match>
                    <Match when={windowState().phase === "ended"}>
                      <p class="text-center text-sm text-muted-foreground" data-testid="live-ended">
                        {t(msg`This class has ended.`)}
                      </p>
                    </Match>
                  </Switch>

                  <Button
                    class="w-full gap-2"
                    disabled={windowState().phase !== "open" || stage() === "joining"}
                    data-testid="live-enter"
                    onClick={() => void enter()}
                  >
                    <Video class="h-4 w-4" />
                    {stage() === "joining" ? t(msg`Connecting…`) : t(msg`Enter`)}
                  </Button>

                  <Show when={error()}>
                    <p class="text-center text-xs text-danger" data-testid="live-error">
                      {error()}
                    </p>
                  </Show>

                  {/* Recording (rewatch) */}
                  <Show when={row()?.recording_video_id}>
                    <div class="flex justify-center border-t border-border pt-3">
                      <RecordingPlayer
                        videoId={row()!.recording_video_id!}
                        title={row()?.topic ?? undefined}
                      />
                    </div>
                  </Show>
                </div>
              </Match>
            </Switch>
          </div>

          {/* Roster sidebar (only populated for the teacher/admin) */}
          <Show when={roster().length > 0}>
            <aside
              class="h-fit space-y-2 rounded-2xl border border-border p-4"
              data-testid="live-roster"
            >
              <h2 class="flex items-center gap-1.5 text-sm font-bold">
                <Users class="h-4 w-4" /> {t(msg`Students`)} ({roster().length})
              </h2>
              <ul class="space-y-1">
                <For each={roster()}>
                  {(student) => (
                    <li
                      class="truncate rounded-lg px-2 py-1 text-sm text-muted-foreground"
                      data-testid="live-roster-row"
                    >
                      {student.name ?? student.email ?? student.user_id}
                    </li>
                  )}
                </For>
              </ul>
            </aside>
          </Show>
        </div>
      </Show>
    </div>
  );
};
